/**
 * Krok CER1 bod 5 — REDAKCE HLASENI CHYB Z PROHLIZECE.
 *
 * Z prohlizece makleře odchazi hlaseni chyby do NASI DB (`client_error_events`,
 * `POST /v1/client-errors`). Chyba pritom casto nese to, co makler zrovna
 * vyplnoval: „Nelze uložit: jan.novak@example.com" nebo kus odpovedi API.
 * Tenhle modul je JEDINE misto, kterym text hlaseni projde, a zaroven jediny
 * zdroj otisku (fingerprint) a sablony cesty.
 *
 * PROC NE `redactText` z `@reos/observability`: ta redakce chrani SERVEROVY
 * LOG, kam smi jen identifikator — ma jine znacky (`<EMAIL>`) a hlavne
 * nezakryva obecne cislo ani text v uvozovkach. Tady je rezim STRIKTNEJSI
 * (kazda sekvence 6+ cislic a kazdy delsi text v uvozovkach padne), protoze
 * zdroj je neznamy retezec z prohlizece, ne nas vlastni log. Krome toho balik
 * `@reos/observability` tahne `pino` (Node), takze do prohlizece nepatri.
 * Rozdil je zamerny — viz REPORT-step-CER1.md „Kde jinde žije stejný koncept".
 *
 * CO SE NIKDY NESBIRA (a tedy ani neredaguje, protoze to modul nedostane):
 * telo pozadavku ani odpovedi, hlavicky, cookies, dotaz a fragment URL
 * (jezdi v nich jednorazove odkazy k podpisu), hodnoty formularu,
 * localStorage, text DOMu. Sber je v `report-collector.ts`.
 *
 * UUID ZUSTAVA: identifikuje ZAZNAM (zakazku, smlouvu), ne osobu — a bez nej
 * nejde chybu dohledat. Proto se pred redakci cisel odlozi a po ni vrati.
 */

/** Druhy udalosti — tataz mnozina jako `client_error_events_kind_check` v migraci 0056. */
export const CLIENT_ERROR_KINDS = ['js', 'promise', 'boundary', 'api', 'ui_error', 'unsaved'] as const;
export type ClientErrorKind = (typeof CLIENT_ERROR_KINDS)[number];

/** Strop delky `message` — shodny se zod schematem routy a sloupcem v DB. */
export const MAX_MESSAGE_LENGTH = 300;
/** Kolik ramcu stacku se posila (bod 5: „jen prvních 5"). */
export const MAX_STACK_FRAMES = 5;

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * POZADI JE ZAVAZNE (kryto testem `client-error-scrub.spec.ts`):
 *   - text v uvozovkach jde PRVNI: je to nejsirsi vzor a typicky v nem jezdi
 *     cela veta z formulare („Hodnota „Jan Novák, Dlouhá 5" není platná"),
 *   - e-mail pred telefonem a obecnym cislem (jinak by se rozpadl na kousky),
 *   - IBAN pred rodnym cislem i uctem,
 *   - rodne cislo PRED cislem uctu (`000101/0000` sedi na oba vzory) a pred
 *     telefonem (jinak by z devaticiferneho tvaru zbylo `[tel]`),
 *   - telefon pred obecnym cislem (jinak by z nej zbylo `[cislo]`),
 *   - obecna sekvence 6+ cislic az nakonec jako posledni zachytka.
 *
 * ZADNY `lookbehind` (`(?<=…)`): Safari ho umi az od 16.4 a starsi prohlizec
 * by na takovem vzoru spadl uz pri nacteni balicku — tedy presne tam, kde
 * hlaseni chyb ma fungovat. Hranice vlevo se proto resi zachytnou skupinou.
 */
const PATTERNS: readonly (readonly [RegExp, string])[] = [
  // Text v uvozovkach delsi nez 20 znaku — rovne, apostrofy i ceske „ “.
  [/"[^"]{21,}"/g, '[text]'],
  [/„[^“]{21,}“/g, '[text]'],
  [/'[^']{21,}'/g, '[text]'],
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]'],
  // IBAN (CZ6508000000192000145399) i mezinarodni tvar s mezerami.
  [/\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]{4}){2,7}\b/g, '[ucet]'],
  // Rodne cislo (000101/0000). Bez lomitka je to 9-10 cislic a padne nize
  // jako `[tel]`/`[cislo]` — zadny udaj tim neunikne.
  [/(^|[^\d/])(\d{6}\/\d{3,4})(?![\d/])/g, '$1[rc]'],
  // Cislo uctu v ceskem tvaru: 19-2000145399/0800, 2000145399/0800.
  [/(^|[^\d/-])((?:\d{2,6}-)?\d{2,10}\/\d{4})(?![\d/])/g, '$1[ucet]'],
  // Telefon: +420 777 123 456, 00420777123456, 777 123 456, 777123456.
  [/(?:\+|00)\d{1,3}[ ]?(?:\d[ ]?){8,11}\d/g, '[tel]'],
  [/\b\d{3}[ ]?\d{3}[ ]?\d{3}\b/g, '[tel]'],
  // Posledni zachytka: jakakoli sekvence 6+ cislic (cislo smlouvy, LV, IČO…).
  [/\d{6,}/g, '[cislo]'],
];

/**
 * Zredaguje volny text (hlaska chyby). Vraci nejvyse `MAX_MESSAGE_LENGTH`
 * znaku — strop je tady, aby ho nebylo mozne obejit zapomenutim u volajiciho.
 */
export function scrubText(input: string): string {
  const uuids: string[] = [];
  const stashed = input.replace(UUID_RE, (match) => {
    uuids.push(match);
    // Znacka z PRIVATE USE AREA: v hlasce chyby se nevyskytuje a neni to
    // ridici znak (eslint `no-control-regex`).
    return `\uE000U${String(uuids.length - 1)}\uE000`;
  });
  const redacted = PATTERNS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    stashed,
  );
  const restored = redacted.replace(/\uE000U(\d+)\uE000/g, (_match, index: string) => {
    const uuid = uuids[Number(index)];
    return uuid ?? '';
  });
  return restored.replace(/\s+/g, ' ').trim().slice(0, MAX_MESSAGE_LENGTH);
}

/**
 * Ze stacku zbyde nejvyse pet radku `soubor:radek:sloupec`. Nazev funkce ani
 * nic v zavorkach se nebere — v minifikovanem balicku to nic nerika a muze to
 * nest hodnotu (`at saveContract (… "Jan Novák" …)`).
 */
export function scrubStack(stack: string | undefined): string {
  if (stack === undefined || stack === '') return '';
  const frames: string[] = [];
  for (const line of stack.split('\n')) {
    const matched = /([^\s()[\]]+):(\d+):(\d+)/.exec(line);
    if (matched === null) continue;
    const file = (matched[1] ?? '').split('?')[0] ?? '';
    const base = file.split('/').pop() ?? file;
    if (base === '') continue;
    frames.push(`${base}:${String(matched[2])}:${String(matched[3])}`);
    if (frames.length >= MAX_STACK_FRAMES) break;
  }
  return frames.join(' | ').slice(0, MAX_MESSAGE_LENGTH);
}

/** Je segment cesty identifikator zaznamu (UUID, cislo, dlouhy hex/slug s cislem)? */
function isIdSegment(segment: string): boolean {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true;
  if (/^\d+$/.test(segment)) return true;
  return /^[0-9a-f]{16,}$/i.test(segment);
}

export interface RouteTemplate {
  /** Cesta se zastupnymi znacky, napr. `/smlouvy/$id` (seskupovaci klic). */
  readonly route: string;
  /** Prvni UUID z cesty — na JAKEM zaznamu chyba vznikla, nebo `null`. */
  readonly entityId: string | null;
}

/**
 * Z adresy obrazovky udela SABLONU. Dotaz a fragment se zahazuji uz tady
 * (bod 5: jezdi v nich jednorazove odkazy k podpisu) — funkce bere jen
 * `pathname`, nikdy celou adresu.
 */
export function routeTemplateOf(pathname: string): RouteTemplate {
  const segments = pathname.split('/');
  let entityId: string | null = null;
  const template = segments
    .map((segment) => {
      if (segment === '' || !isIdSegment(segment)) return segment;
      if (entityId === null && segment.includes('-')) entityId = segment;
      return '$id';
    })
    .join('/');
  return { route: (template === '' ? '/' : template).slice(0, 200), entityId };
}

/** Adresa API bez dotazu a fragmentu — relativni cesta, ne cely origin. */
export function apiPathOf(rawUrl: string, origin: string): string {
  const withoutHash = rawUrl.split('#')[0] ?? rawUrl;
  const withoutQuery = withoutHash.split('?')[0] ?? withoutHash;
  const path = withoutQuery.startsWith(origin) ? withoutQuery.slice(origin.length) : withoutQuery;
  return path.slice(0, 300);
}

/**
 * Prohlizec a system v krotkem tvaru („Chrome 131 / Windows"). Cely UA string
 * se neposila — je to otisk prohlizece (fingerprinting), a nam staci vedet,
 * na cem to nefunguje.
 */
export function uaShort(userAgent: string): string {
  const browsers: readonly (readonly [RegExp, string])[] = [
    [/Edg\/(\d+)/, 'Edge'],
    [/OPR\/(\d+)/, 'Opera'],
    [/Firefox\/(\d+)/, 'Firefox'],
    [/Chrome\/(\d+)/, 'Chrome'],
    [/Version\/(\d+).*Safari/, 'Safari'],
  ];
  let browser = 'neznámý';
  for (const [pattern, name] of browsers) {
    const matched = pattern.exec(userAgent);
    if (matched !== null) {
      browser = `${name} ${matched[1] ?? ''}`.trim();
      break;
    }
  }
  const systems: readonly (readonly [RegExp, string])[] = [
    [/iPhone|iPad|iPod/, 'iOS'],
    [/Android/, 'Android'],
    [/Windows/, 'Windows'],
    [/Mac OS X/, 'macOS'],
    [/Linux/, 'Linux'],
  ];
  const system = systems.find(([pattern]) => pattern.test(userAgent))?.[1] ?? 'neznámý';
  return `${browser} / ${system}`.slice(0, 80);
}

/** Vstup pro otisk — jen pole, ktera udalost IDENTIFIKUJI, ne jeji cas. */
export interface FingerprintInput {
  readonly kind: ClientErrorKind;
  readonly route: string;
  readonly message: string;
  readonly apiMethod?: string | null;
  readonly apiPath?: string | null;
  readonly apiStatus?: number | null;
  readonly errorCode?: string | null;
}

/**
 * Otisk udalosti. Deduplikace v zalozce (bod 2) i seskupeni ve vypisu (bod 6)
 * se dela PRESNE podle nej — proto je tu jedna funkce a ne dve pravdy.
 * Cisla v hlasce se pred otiskem srovnavaji na `#`, aby „řádek 12" a
 * „řádek 13" byla tataz chyba.
 */
export function fingerprintOf(input: FingerprintInput): string {
  const normalized = [
    input.kind,
    input.route,
    input.apiMethod ?? '',
    input.apiPath ?? '',
    input.apiStatus === null || input.apiStatus === undefined ? '' : String(input.apiStatus),
    input.errorCode ?? '',
    input.message.replace(/\d+/g, '#').slice(0, 160),
  ].join('|');
  // djb2 — staci na seskupeni (ne na bezpecnost) a nepotrebuje crypto API,
  // ktere v nativnim obalu nemusi byt dostupne.
  let hash = 5381;
  for (let index = 0; index < normalized.length; index += 1) {
    hash = ((hash << 5) + hash + normalized.charCodeAt(index)) | 0;
  }
  return `${input.kind}-${(hash >>> 0).toString(36)}`;
}
